import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';

/**
 * DOC4 - type a client's name and it fills from the app's own contacts.
 *
 * His words, 2026-09-04: *"if I have clients, I can just somehow type client's name and it auto
 * fills from GHL/app that syncs with GHL."*
 *
 * ---------------------------------------------------------------------------------------------
 * ⭐⭐⭐ MEASURED FIRST, AND MOST OF IT ALREADY EXISTED (2026-09-05, live)
 * ---------------------------------------------------------------------------------------------
 *     contacts                    7,048 rows       ghl_contacts 7,745
 *     ghl rows LINKED to a contact    7,745 of 7,745        last synced TODAY
 *     GET /api/contacts?search=   already server-side, paged, capped at 100
 *
 * **So this makes NO GHL call.** The mirror is already in Supabase and current, which is what
 * `reference_ghl_mcp_accounts` says to prefer - several GHL accounts are connected and only one is
 * Apex, so the safest call is the one you do not make. What was missing was only this component.
 *
 * ⛔⛔ AND THE SHAPE OF THE DATA DECIDED THE DESIGN. **6,979 of the 7,048 contacts are
 * `contact_type: 'other'`** - the bulk prospecting import, mostly phone-only. His actual clients
 * are 43 buyers and 12 sellers. Ranking them equally would bury the person he means under a
 * thousand cold prospects, so typed contacts sort FIRST and every row wears its type.
 *
 * ⛔⛔ IT STAYS A TEXT FIELD, NOT A SELECT. A buyer is often an ENTITY - "MDTH VICTORVILLE LLC" -
 * and `companies` holds exactly ONE row, so most entities are not contacts at all. The suggestions
 * are an offer, never a gate.
 */

interface ContactRow {
  id: string;
  first_name: string | null;
  last_name: string | null;
  contact_type: string | null;
  email: string | null;
  phone: string | null;
  mobile_phone: string | null;
  title: string | null;
}

/** ⭐ `other` is the prospecting import; everything else is somebody he has classified. */
const rank = (t: string | null): number => (t && t !== 'other' ? 0 : 1);

const fullName = (c: ContactRow): string =>
  [c.first_name, c.last_name].filter(Boolean).join(' ').trim();

const reachBy = (c: ContactRow): string | null => c.email || c.phone || c.mobile_phone || null;

const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;

export default function ContactPicker({
  name,
  label,
  required,
  placeholder,
  defaultValue,
}: {
  name: string;
  label: string;
  required?: boolean;
  placeholder?: string;
  defaultValue?: string;
}) {
  const [value, setValue] = useState(defaultValue ?? '');
  const [options, setOptions] = useState<ContactRow[]>([]);
  const [open, setOpen] = useState(false);
  /** Set while a chosen value is being written, so choosing does not immediately re-search. */
  const justChose = useRef(false);

  const search = useCallback(async (term: string) => {
    try {
      const r = await api.listContacts({ search: term, limit: '25' });
      const rows = ((r?.contacts ?? []) as ContactRow[]).filter((c) => fullName(c));
      // ⭐ Ranked here rather than in the query: PostgREST cannot order by a CASE, and the page is
      // 25 rows - sorting what came back is honest at that size and needs no new endpoint.
      rows.sort((a, b) => rank(a.contact_type) - rank(b.contact_type)
        || fullName(a).localeCompare(fullName(b)));
      setOptions(rows);
      setOpen(rows.length > 0);
    } catch {
      // ⛔ A FAILED LOOKUP MUST NOT COST HIM THE FIELD HE IS TYPING IN. The suggestions are a
      // convenience; the text he has typed is the actual work.
      setOptions([]);
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    if (justChose.current) { justChose.current = false; return; }
    const term = value.trim();
    // A keystroke is not a query. 7,048 rows behind an un-debounced box is a request per letter.
    if (term.length < MIN_CHARS) { setOptions([]); setOpen(false); return; }
    const t = setTimeout(() => void search(term), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [value, search]);

  const choose = (c: ContactRow) => {
    justChose.current = true;
    setValue(fullName(c));
    setOpen(false);
  };

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor={`cp-${name}`}>
        {label}{required ? ' *' : ''}
      </label>
      <input
        id={`cp-${name}`}
        name={name}
        required={required}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onFocus={() => { if (options.length) setOpen(true); }}
        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
      />

      {open && (
        <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
          {options.map((c) => {
            const reach = reachBy(c);
            return (
              <li key={c.id} data-contact-option>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => choose(c)}
                  className="flex w-full flex-wrap items-baseline gap-x-2 px-3 py-2 text-left hover:bg-gray-50"
                >
                  <span className="text-[13px] font-medium text-gray-800">{fullName(c)}</span>
                  {/* ⭐ The TYPE on every row: 6,979 of 7,048 are the prospecting import, and a cold
                      prospect must never be mistaken for a client on an offer letter. */}
                  <span className="rounded border border-gray-300 bg-gray-50 px-1.5 py-0.5 text-[10.5px] text-gray-600">
                    {c.contact_type ?? 'other'}
                  </span>
                  {reach && <span className="text-[11.5px] text-gray-400">{reach}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
